import React, { PureComponent, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Swipeable from 'react-swipeable';
import { groupBy } from 'lodash';
import Slideshow from '@fiverr-private/slideshow/src/Slideshow';
import { GIG_GALLERY } from '../../../utils/events';
import { MP_CLICKED_GALLERY_FULL_SCREEN } from '../../../utils/mixPanelEvents';
import { isTouchDevice } from '../../../utils/isTouchDevice';
import * as Slides from './slides';
import GalleryThumbnails from './GalleryThumbnails';
import { keyCode } from './constants';
import IndicationDots from './IndicationDots';

const GalleryModal = lazy(() => import(/* webpackChunkName: 'GalleryModal' */'./GalleryModal'));

import './style.scss';

// The number of images ahead of the current slider to preload...
const LOAD_AHEAD = 1;

class GigGallery extends PureComponent {

    constructor(props) {
        super(props);

        this.state = {
            currSlideIndex: 0,
            isTouch: this.props.isTouch,
            renderGalleryModal: false
        };

        // Refs
        this.componentWrapper = null;
        this.galleryModal = null;

        this.slideComponents = [];

        // Binding
        this.renderSlide = this.renderSlide.bind(this);
        this.changeSlide = this.changeSlide.bind(this);
        this.handleSlideClick = this.handleSlideClick.bind(this);
        this.handlePrev = this.handlePrev.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleThumbnailChange = this.handleThumbnailChange.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    componentDidMount() {
        // eslint-disable-next-line
        this.setState({
            isTouch: isTouchDevice(),
            renderGalleryModal: true
        });
    }

    changeSlide(index, callback) {
        const { length: len } = this.props.slides;
        let nextSlide = index;

        if (nextSlide < 0) {
            nextSlide = len - 1;
        }
        if (nextSlide >= len || typeof index !== 'number') {
            nextSlide = 0;
        }

        this.setState({
            currSlideIndex: nextSlide
        }, callback);

        this.componentWrapper && this.componentWrapper.focus({ preventScroll: true });
    }

    handlePrev(e) {
        const { biEvents } = this.context;
        e && e.preventDefault && e.preventDefault();
        this.changeSlide(this.state.currSlideIndex - 1, () => {
            biEvents.sendBigQueryEvent({
                eventName: GIG_GALLERY,
                params: { type:'clicked_previous', source: 'gig page' }
            });
        });
    }

    handleNext(e) {
        const { biEvents } = this.context;
        e && e.preventDefault && e.preventDefault();
        this.changeSlide(this.state.currSlideIndex + 1, () => {
            biEvents.sendBigQueryEvent({
                eventName: GIG_GALLERY,
                params: { type:'clicked_next', source: 'gig page' }
            });
        });
    }

    handleThumbnailChange(idx) {
        const { biEvents } = this.context;
        this.changeSlide(idx, () => {
            biEvents.sendBigQueryEvent({
                eventName: GIG_GALLERY,
                params: { type:'clicked_image', source: 'zoom_popup' }
            });
        });
    }

    handleKeyDown(e) {
        if (!e) { return; }
        (e.keyCode === keyCode.ARROW_LEFT) && this.handlePrev(e);
        (e.keyCode === keyCode.ARROW_RIGHT) && this.handleNext(e);
    }


    reportToMixPanel() {
        const { slides } = this.props;
        const { biEvents } = this.context;
        const types = groupBy(slides, (slide) => slide.itemScopeType);
        const { ImageSlide, VideoSlide, PdfSlide, AudioSlide } = types;

        biEvents.sendMixPanelEvent({
            eventName: MP_CLICKED_GALLERY_FULL_SCREEN,
            params: {
                'Number of items': slides.length,
                'image': ImageSlide ? ImageSlide.length : 0,
                'video': VideoSlide ? VideoSlide.length : 0,
                'pdf': PdfSlide ? PdfSlide.length : 0,
                'audio': AudioSlide ? AudioSlide.length : 0
            }
        });
    }

    handleSlideClick(index) {
        const slide = this.props.slides[index];
        const { biEvents } = this.context;

        // Only show modal if the current slide being clicked is an img or a pdf
        if ((slide.typeImage || slide.typePdf) && this.galleryModal) {
            this.galleryModal.open();
            biEvents.sendBigQueryEvent({ eventName: GIG_GALLERY, params: { type:'clicked_zoom', source: 'gig page' } });
            this.reportToMixPanel();
        }
    }

    renderSlide(slide, index) {
        const { currSlideIndex } = this.state,
            slideProps = {
                slide,
                key: `${index}--${slide.media.medium || slide.src}`,
                isActive: (index === currSlideIndex),
                expanded: false,

                // For now, this is pretty dumb "one ahead" preloader, if we need to make it more
                // complex we need to move this logic to it's own function.
                preload: (index > currSlideIndex && index <= currSlideIndex + LOAD_AHEAD)
            };

        if (this.slideComponents[index]) {
            const { isActive, preload } = this.slideComponents[index].props;
            if (
                isActive === slideProps.isActive &&
                preload === slideProps.preload
            ) {
                return this.slideComponents[index];
            }
        }

        // eslint-disable-next-line import/namespace
        const SlideComponent = Slides[slide.itemScopeType];
        return SlideComponent ? <SlideComponent {...slideProps}/> : null;
    }

    render() {
        const { transition, slides } = this.props;
        const { isTouch, renderGalleryModal } = this.state;
        const hasSlides = !!slides.length,
            { currSlideIndex } = this.state,
            arrows = [
                (<a key="ss-prev" href="#" className="nav-prev" onClick={this.handlePrev} rel="nofollow"/>),
                (<a key="ss-next" href="#" className="nav-next" onClick={this.handleNext} rel="nofollow"/>)
            ];
        this.slideComponents = slides.map(this.renderSlide);
        const sectionClasses = classNames('gig-gallery-component', { 'touch': isTouch });

        if (!hasSlides) {return null;}

        return (
            <section className={sectionClasses}
                ref={(el) => this.componentWrapper = el}
                onKeyDown={this.handleKeyDown} tabIndex={-1}>
                <div className="gallery-slideshow">
                    {(slides.length > 1) && arrows}
                    <Swipeable onSwipedLeft={this.handleNext}
                        onSwipedRight={this.handlePrev}
                        className="swipe-events-wrapper">
                        <Slideshow selectedIndex={currSlideIndex}
                            transition={transition}
                            onClick={this.handleSlideClick}>
                            {this.slideComponents}
                        </Slideshow>
                    </Swipeable>
                </div>
                <IndicationDots count={slides.length} current={currSlideIndex}/>
                <GalleryThumbnails slides={slides}
                    selectedIndex={currSlideIndex}
                    onChange={this.handleThumbnailChange}/>
                { renderGalleryModal &&
                <Suspense fallback={<div/>}>
                    <GalleryModal slides={slides}
                        selectedIndex={currSlideIndex}
                        transition="fade-slide-h"
                        onClose={this.changeSlide} ref={(ref) => this.galleryModal = ref}/>
                </Suspense>
                }
            </section>
        );
    }
}

GigGallery.propTypes = {
    slides: PropTypes.array.isRequired,
    transition: PropTypes.string,
    isTouch: PropTypes.bool
};

GigGallery.defaultProps = {
    transition: 'slide-h',
    isTouch: false
};

GigGallery.contextTypes = {
    biEvents: PropTypes.object
};

export default GigGallery;
